"use client";

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/components/providers/auth-provider";
import { ApiError } from "@/lib/api";
import { formatDate } from "@/lib/format-datetime";
import { queryKeys } from "@/lib/query-keys";
import { useDebouncedValue } from "@/features/playground/lib/use-debounced-value";
import { changeMyUsername, checkUsernameAvailability, fetchMyUsername, updateProfile } from "../api";
import { guessCountryFromTimeZone, isCountryCode, type CountryCode } from "../lib/countries";
import { CountryFlag } from "./CountryFlag";
import { CountryPicker } from "./CountryPicker";
import { InstitutionInput } from "./InstitutionInput";
import { MY_USERNAME_KEY } from "./UsernameCard";

const USERNAME_SHAPE = /^[A-Za-z0-9][A-Za-z0-9_-]{1,18}[A-Za-z0-9]$/;

/**
 * `EX-IDN05` and `EX-IDN06` (ADR-0088 decisions 2 and 4): the three things a
 * contest shows, in one form. The Welcome screen shows it after sign-up, and
 * a contest's registration box shows it when something is missing.
 *
 * Username (the current one filled in; keeping it confirms it), country (the
 * saved one, or Bangladesh when the device keeps Bangladesh time), institution
 * (suggested), and a phone, optional, only when the account has none. Save
 * needs the three; the phone never holds it up. A line on top shows the row
 * the standings will print.
 */
export function ContestProfileForm({
  locale,
  onSaved,
  submitLabel,
  secondary,
}: {
  locale: string;
  /** After everything saved. */
  onSaved: () => void | Promise<void>;
  /** The Save button's words; the form's own "Save" when absent. */
  submitLabel?: string;
  /** A second action beside Save (the Welcome screen's "Later"). */
  secondary?: React.ReactNode;
}) {
  const t = useTranslations("contestProfile");
  const { user, getToken } = useAuth();
  const queryClient = useQueryClient();
  const mine = useQuery({ queryKey: MY_USERNAME_KEY, queryFn: fetchMyUsername, staleTime: 30_000 });

  const [usernameDraft, setUsernameDraft] = useState<string | null>(null);
  const [countryDraft, setCountryDraft] = useState<CountryCode | null | undefined>(undefined);
  const [institutionDraft, setInstitutionDraft] = useState<string | null>(null);
  const [phone, setPhone] = useState("");
  const [askPhone] = useState(() => !user?.phone);

  const username = usernameDraft ?? mine.data?.usernameDisplay ?? "";
  const trimmedUsername = username.trim();
  const countryCode =
    countryDraft !== undefined ? countryDraft : isCountryCode(user?.countryCode) ? user.countryCode : guessCountryFromTimeZone();
  const institution = institutionDraft ?? user?.institution ?? "";

  const sameKey = mine.data !== undefined && trimmedUsername.toLowerCase() === mine.data.username;
  const shapeOk = sameKey || USERNAME_SHAPE.test(trimmedUsername);
  const windowClosed = !sameKey && mine.data?.usernameNextChangeAt ? Date.parse(mine.data.usernameNextChangeAt) > Date.now() : false;
  const debounced = useDebouncedValue(trimmedUsername, 400);
  const availability = useQuery({
    queryKey: [...MY_USERNAME_KEY, "availability", debounced],
    queryFn: () => checkUsernameAvailability(debounced),
    enabled: mine.data !== undefined && !sameKey && shapeOk && debounced === trimmedUsername,
    staleTime: 30_000,
  });
  const usernameOk =
    mine.data !== undefined &&
    shapeOk &&
    !windowClosed &&
    (sameKey || (availability.data?.available === true && availability.data.username.toLowerCase() === trimmedUsername.toLowerCase()));

  const save = useMutation({
    mutationFn: async () => {
      const token = getToken();
      if (!token) throw new Error("Not signed in");
      // Keeping the name, or choosing a new one, stamps it as the person's own.
      if (!mine.data?.usernameChosen || !sameKey || trimmedUsername !== mine.data.usernameDisplay) {
        const next = await changeMyUsername(trimmedUsername);
        queryClient.setQueryData(MY_USERNAME_KEY, next);
      }
      await updateProfile(
        {
          countryCode: countryCode ?? undefined,
          institution: institution.trim(),
          ...(askPhone && phone.trim() ? { phone: phone.trim() } : {}),
        },
        token,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
      await onSaved();
    },
  });

  let usernameHint: { tone: "ok" | "bad" | "muted"; text: string } | null = null;
  if (save.error instanceof ApiError && save.error.code === "USERNAME_TAKEN") usernameHint = { tone: "bad", text: t("usernameTaken") };
  else if (!trimmedUsername) usernameHint = null;
  else if (sameKey) usernameHint = { tone: "muted", text: t("usernameCurrent") };
  else if (!shapeOk) usernameHint = { tone: "bad", text: t("usernameInvalid") };
  else if (windowClosed && mine.data?.usernameNextChangeAt)
    usernameHint = { tone: "bad", text: t("usernameWindow", { date: formatDate(mine.data.usernameNextChangeAt, locale) }) };
  else if (availability.data && availability.data.username.toLowerCase() === trimmedUsername.toLowerCase())
    usernameHint = availability.data.available
      ? { tone: "ok", text: t("usernameAvailable") }
      : { tone: "bad", text: availability.data.reason === "reserved" ? t("usernameReserved") : t("usernameTaken") };
  else usernameHint = { tone: "muted", text: t("usernameChecking") };

  const canSave = usernameOk && Boolean(countryCode) && institution.trim().length > 0 && !save.isPending;
  const hintClass = (tone: "ok" | "bad" | "muted") =>
    tone === "ok" ? "text-xs text-emerald-700" : tone === "bad" ? "text-xs text-red-600" : "text-xs text-neutral-500";

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSave) save.mutate();
      }}
    >
      <div aria-live="polite" className="flex items-center gap-2.5 rounded-[var(--radius)] bg-neutral-100 px-3 py-2.5 text-sm">
        <span className="text-neutral-500">{t("previewLabel")}</span>
        <CountryFlag code={countryCode} locale={locale} />
        <span className="font-bold text-neutral-900">{trimmedUsername || "..."}</span>
        {institution.trim() ? <span className="truncate text-neutral-500">{institution.trim()}</span> : null}
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="cp-username" className="text-sm font-semibold text-neutral-900">
          {t("usernameLabel")}
        </label>
        <Input
          id="cp-username"
          value={username}
          maxLength={40}
          autoComplete="username"
          spellCheck={false}
          aria-describedby="cp-username-hint"
          onChange={(e) => {
            setUsernameDraft(e.target.value);
            save.reset();
          }}
        />
        <p id="cp-username-hint" className={usernameHint ? hintClass(usernameHint.tone) : "text-xs text-neutral-500"}>
          {usernameHint?.text ?? t("usernameHint")}
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="cp-country" className="text-sm font-semibold text-neutral-900">
          {t("countryLabel")}
        </label>
        <CountryPicker
          id="cp-country"
          value={countryCode}
          onChange={setCountryDraft}
          locale={locale}
          labels={{ placeholder: t("countryPlaceholder"), noMatch: t("countryNoMatch") }}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="cp-institution" className="text-sm font-semibold text-neutral-900">
          {t("institutionLabel")}
        </label>
        <InstitutionInput
          id="cp-institution"
          value={institution}
          onChange={setInstitutionDraft}
          labels={{ placeholder: t("institutionPlaceholder"), selfTaught: t("selfTaught") }}
        />
      </div>

      {askPhone ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="cp-phone" className="text-sm font-semibold text-neutral-900">
            {t("phoneLabel")} <span className="font-normal text-neutral-500">{t("phoneOptional")}</span>
          </label>
          <Input
            id="cp-phone"
            type="tel"
            autoComplete="tel"
            maxLength={20}
            placeholder={t("phonePlaceholder")}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
      ) : null}

      {save.error && !(save.error instanceof ApiError && save.error.code === "USERNAME_TAKEN") ? (
        <p role="alert" className="text-sm text-red-600">
          {t("error")}
        </p>
      ) : null}

      <div className="flex flex-col gap-2">
        <Button type="submit" disabled={!canSave}>
          {save.isPending ? t("saving") : (submitLabel ?? t("save"))}
        </Button>
        {secondary}
      </div>
    </form>
  );
}
